/**
 * Single source of truth for the AI-extracted contract clause sections.
 *
 * Each clause has three identifiers that used to be duplicated across the
 * new-contract form, the read-only contract Provisions tab, and the AI analysis
 * dialog. Adding a clause meant editing all of them, and the lists drifted — a
 * clause would extract + save but never render (exactly the "Provisions tab is
 * missing clauses" bug). Everything now derives from CLAUSE_SECTIONS, so adding
 * a clause is a one-line change here.
 *
 *  - keySectionKey: key inside the AI `extraction.keySections` blob (must match
 *                   ExtractedKeySections in document-analysis.service.ts)
 *  - contractField: flat field on Contract where the clause language is persisted
 *                   (must match the `*Provision` fields in contract.ts)
 *  - label:         human label shown in the UI
 *
 * Invariant: contractField === keySectionKey + 'Provision'. Kept explicit for
 * grep-ability and so a rename on either side is a visible, deliberate edit.
 */
export interface ClauseSection {
  keySectionKey: string;
  contractField: string;
  label: string;
}

export const CLAUSE_SECTIONS: ClauseSection[] = [
  { keySectionKey: 'term',                  contractField: 'termProvision',                  label: 'Term' },
  { keySectionKey: 'indemnification',       contractField: 'indemnificationProvision',       label: 'Indemnification' },
  { keySectionKey: 'termination',           contractField: 'terminationProvision',           label: 'Termination' },
  { keySectionKey: 'nonCompete',            contractField: 'nonCompeteProvision',            label: 'Non-Compete' },
  { keySectionKey: 'paymentTerms',          contractField: 'paymentTermsProvision',          label: 'Payment Terms' },
  { keySectionKey: 'compensation',          contractField: 'compensationProvision',          label: 'Compensation' },
  { keySectionKey: 'liabilityLimitations',  contractField: 'liabilityLimitationsProvision',  label: 'Limitation of Liability' },
  { keySectionKey: 'insuranceRequirements', contractField: 'insuranceRequirementsProvision', label: 'Insurance Requirements' },
  { keySectionKey: 'confidentiality',       contractField: 'confidentialityProvision',       label: 'Confidentiality' },
  { keySectionKey: 'disputeResolution',     contractField: 'disputeResolutionProvision',     label: 'Dispute Resolution' },
  { keySectionKey: 'costsFeesPricing',      contractField: 'costsFeesPricingProvision',      label: 'Costs, Fees & Pricing' },
  { keySectionKey: 'forceMajeure',          contractField: 'forceMajeureProvision',          label: 'Force Majeure' },
  { keySectionKey: 'governingLaw',          contractField: 'governingLawProvision',          label: 'Governing Law & Jurisdiction' },
  { keySectionKey: 'remedies',              contractField: 'remediesProvision',              label: 'Remedies' },
  { keySectionKey: 'warranty',              contractField: 'warrantyProvision',              label: 'Warranty' },
];

/** keySectionKey → label. For consumers that key off the AI keySections blob
 *  (new-contract intake form, AI analysis dialog). */
export const CLAUSE_LABELS_BY_KEYSECTION: { [keySectionKey: string]: string } =
  CLAUSE_SECTIONS.reduce((acc, s) => { acc[s.keySectionKey] = s.label; return acc; }, {} as { [k: string]: string });

/** contractField → label. For consumers that key off the persisted contract
 *  fields (read-only contract Provisions tab). */
export const CLAUSE_LABELS_BY_FIELD: { [contractField: string]: string } =
  CLAUSE_SECTIONS.reduce((acc, s) => { acc[s.contractField] = s.label; return acc; }, {} as { [k: string]: string });
